import React, {FC} from 'react';

import {DateRangeComboBox} from '../../../../components/common/dateTime/dateRangeComboBox';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {StartAndEndDates, StartAndEndDatesWithPeriod} from '../../../../models/dates/dateIntervalModel';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getCalendarWeekStartDay} from '../../../../store/data/preferences/preferencesSelectors';
import {getUnsavedViewNavigationBlockerId} from '../../../../store/panels/analytics/analyticsPanelSelectors';
import {
  getAnalyticsDatetimes,
  getAnalyticsParameters,
} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchVisibleMetricsForReport} from '../../../../store/panels/analytics/report/analyticsPanelReportActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {makeAnalyticsDateTimesUrl} from '../../../helpers/analyticsUrlHelpers';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';

/*
 * Props.
 */

interface ReportHeaderDatepickerProps {}

export const ReportHeaderDatepicker: FC<ReportHeaderDatepickerProps> = () => {
  const dispatch = useAppDispatch();
  const trackAnalytics = useAnalyticsTracking();
  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);
  const parameters = useAppSelector(getAnalyticsParameters);
  const calendarWeekStartDay = useAppSelector(getCalendarWeekStartDay);
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);

  const onDateSelect = async (dates: StartAndEndDatesWithPeriod) => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DATE_RANGE_SELECT, {
      start_date: dates.startDate?.toISO(),
      end_date: dates.endDate?.toISO(),
      period: dates.period ?? 'custom',
    });

    if (!hasAllDates(dates)) {
      return;
    }

    // If dates changed, we need to update report data.
    if (
      (dates.startDate && !startDate?.equals(dates.startDate)) ||
      (dates.endDate && !endDate?.equals(dates.endDate))
    ) {
      // We should ignore blocking navigation for report type and dates changes
      // because they are not stored in views.
      const path = makeAnalyticsDateTimesUrl(dates, parameters);
      await dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
      dispatch(fetchVisibleMetricsForReport());
    }
  };

  const onOpen = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DATE_RANGE_CLICK);
  };

  return (
    <DateRangeComboBox
      selectedStartDate={startDate}
      selectedEndDate={endDate}
      calendarWeekStartDay={calendarWeekStartDay}
      onDateSelect={onDateSelect}
      onOpen={onOpen}
      periodSelectionChevronsInButton
    />
  );
};

/*
 * Helpers.
 */

function hasAllDates(dates: StartAndEndDates): dates is Required<StartAndEndDates> {
  return Boolean(dates.startDate && dates.endDate);
}
